// t() reads the page language, which the compiler cannot see, and this stays
// on screen while the language changes.
"use no memo";
import { useQuery } from "@tanstack/react-query";
import ArrowLeft from "~icons/ph/arrow-left";
import CaretLeft from "~icons/ph/caret-left";
import ClockHistory from "~icons/ph/clock-counter-clockwise";
import Mic from "~icons/ph/microphone";
import UserPlus from "~icons/ph/user-plus";
import type { CallMedia } from "../../shared/types";
import { AccountReminder, HomePasskey } from "../account/account-recovery";
import { ApiError } from "../data/api";
import { useJoinCallLink, useLogout, useSubmitting } from "../data/mutations";
import { inviteOptions } from "../data/queries";
import {
  useAccount,
  useGuest,
  useProtection,
  useSnapshot,
} from "../data/session";
import { t, useLocale } from "../i18n";
import { StartGroupCall } from "../group-calls/group-link-screen";
import { CallInvite, CallLinkPanel } from "../links/call-link";
import { GuestCall } from "../links/guest-call";
import { personFromHistory, PersonRow } from "../people/person-row";
import { InstallGuide } from "../pwa/install";
import { Avatar, CopyButton, Wave } from "../ui/components";
import { EntryReveal } from "../ui/entry-reveal";
import { notify } from "../ui/toast";
import { HomeGuide } from "./home-guide";
import { useAppLocation } from "./location";
import { useShell } from "./shell";
import { SiteHeader } from "./site-header";

function InviteSection({ linkId }: { linkId: string }) {
  const shell = useShell();
  const account = useAccount();
  const invite = useQuery(inviteOptions(linkId));
  const { protection, failed, retry } = useProtection();
  const joinLink = useJoinCallLink();
  const submitting = useSubmitting();
  const me = account?.me ?? null;
  async function join(media: CallMedia, name: string, token: string) {
    try {
      // Without an account the visitor joins as a guest under this name.
      const { person } = await joinLink.mutateAsync({
        link: linkId,
        ...(me ? {} : { name, turnstileToken: token || undefined }),
      });
      shell.joinCall(person, media);
    } catch (e) {
      // Show why the link stopped working, or the host's current presence.
      if (e instanceof ApiError && [404, 409].includes(e.status))
        void invite.refetch();
      throw e;
    }
  }
  return (
    <CallInvite
      linkId={linkId}
      paused={shell.covered}
      host={invite.data?.host}
      unavailable={
        invite.error instanceof ApiError &&
        [400, 404].includes(invite.error.status)
      }
      failed={invite.isError}
      me={me}
      friend={
        !!account?.friends.some((friend) => friend.id === invite.data?.host.id)
      }
      protection={protection}
      protectionFailed={failed}
      busy={submitting || shell.joining}
      onRetry={() => void invite.refetch()}
      onRetryProtection={retry}
      onRestore={() => shell.open({ kind: "enter", step: "restore" })}
      onJoin={join}
    />
  );
}

// The visitor's and guest's page, a call link, and the signed-in home.
export function HomeScreen() {
  const locale = useLocale();
  const shell = useShell();
  const account = useAccount(),
    guest = useGuest(),
    snapshot = useSnapshot();
  const { inviteId } = useAppLocation();
  const logout = useLogout();
  const submitting = useSubmitting();
  const me = account?.me ?? null;
  const friends = account?.friends ?? [],
    history = account?.history ?? [];
  const online = friends.filter((p) => p.status === "online");
  // The latest people on a call, one face each, lead into the call history.
  const recentPeople = history
    .filter(
      (h, index) =>
        history.findIndex((call) => call.person_id === h.person_id) === index,
    )
    .slice(0, 3)
    .map((h) => personFromHistory(h, friends));
  const signIn = () => shell.open({ kind: "enter", step: "restore" });
  async function leaveGuest() {
    try {
      await logout.mutateAsync();
    } catch (e) {
      notify((e as Error).message);
    }
  }
  return (
    <>
      {!me && (
        <SiteHeader>
          {!guest && !inviteId && (
            <>
              <button className="sign-in-button" onClick={signIn}>
                {t("دخول")}
              </button>
              <button
                className="button enter-button"
                onClick={() => shell.open({ kind: "enter", step: "join" })}
              >
                {t("انضم")}{" "}
                <ArrowLeft className="directional" width={15} height={15} />
              </button>
            </>
          )}
        </SiteHeader>
      )}
      <main id="main-content" tabIndex={-1} className="search-home">
        {guest && !inviteId ? (
          <section
            className="search-hero invite-hero"
            aria-labelledby="invite-title"
          >
            <GuestCall
              guest={guest}
              busy={submitting}
              calling={!!shell.liveCall || shell.joining}
              onCall={(media) => void shell.session.start(guest.host, media)}
              onRetry={() => void snapshot.refetch()}
              onKeep={() => shell.open({ kind: "keep" })}
              onLeave={() => void leaveGuest()}
              onReport={() => shell.report(guest.host)}
            />
            <button
              className="quiet-button"
              onClick={() => shell.open({ kind: "sounds" })}
            >
              {t("أصوات التطبيق")}
            </button>
          </section>
        ) : inviteId ? (
          <section
            className="search-hero invite-hero"
            aria-labelledby="invite-title"
          >
            <InviteSection linkId={inviteId} />
          </section>
        ) : (
          <section className="search-hero" aria-labelledby="home-title">
            {me ? (
              // Signed-in home leads with the account's live line, not the landing pitch.
              <div
                className="home-intro home-presence"
                data-live={shell.connected || undefined}
              >
                <span className="home-presence-avatar">
                  <Avatar person={me} dot />
                </span>
                <div>
                  <h1 id="home-title">
                    {t(
                      shell.connected ? "أنت على الخط" : "جارٍ إعادتك إلى الخط…",
                    )}
                  </h1>
                  <p className="home-handle">
                    <bdi>{me.name}</bdi>
                    <span aria-hidden="true">·</span>
                    <bdi>@{me.username}</bdi>
                    <CopyButton
                      className="icon-button"
                      text={`@${me.username}`}
                      label={t("نسخ اسم المستخدم")}
                      iconOnly
                      iconSize={14}
                      onResult={(copied) => {
                        if (!copied) notify(t("تعذر نسخ اسم المستخدم."));
                      }}
                    />
                  </p>
                </div>
                <HomePasskey />
              </div>
            ) : (
              <div className="home-intro">
                <EntryReveal
                  as="h1"
                  id="home-title"
                  lang={locale}
                  paused={shell.covered}
                  data-ascii={locale === "ar" ? "fade" : undefined}
                >
                  {/* Canvas draws isolated letters; keep Arabic shaping in the DOM. */}
                  <span data-ascii={locale === "ar" ? "skip" : undefined}>
                    {t("ابدأ مكالمة")}
                  </span>
                </EntryReveal>
                <p>
                  {t(
                    "أنشئ رابطًا وأرسله لأي شخص، وتحدثا بالصوت أو الفيديو عندما يفتحه.",
                  )}
                </p>
              </div>
            )}
            <div className="call-link-area">
              <CallLinkPanel
                link={account?.link ?? null}
                paused={shell.covered}
                reachable={!me || me.status === "online"}
                disabled={submitting}
                onCreate={() => {
                  if (me) void shell.createCallLink();
                  else
                    shell.open({ kind: "enter", step: "join", target: "link" });
                }}
                onCancel={() => void shell.cancelCallLink()}
                also={me && <StartGroupCall />}
                aside={
                  me && (
                    <button
                      className="home-history"
                      aria-label={t("سجل المكالمات")}
                      title={t("السجل")}
                      onClick={() => shell.openScreen("history")}
                    >
                      <span className="home-history-clock" aria-hidden="true">
                        <ClockHistory width={20} height={20} />
                      </span>
                      {recentPeople.map((p) => (
                        <Avatar key={p.id} person={p} />
                      ))}
                    </button>
                  )
                }
              />
              {!me && (
                <p className="call-link-reassurance">
                  {t("ابدأ باسم مستعار. دون بريد إلكتروني أو رقم هاتف.")}
                </p>
              )}
            </div>
            {me && (
              <div className="home-people">
                {online.length > 0 && !shell.dialog && (
                  <section
                    className="home-friends"
                    aria-label={t("متصلون الآن")}
                  >
                    <div className="results-label">
                      <span>
                        <i className="online-dot" />
                        {t("متصلون الآن")}
                      </span>
                    </div>
                    {online.slice(0, 3).map((p) => (
                      <PersonRow key={p.id} person={p} kind="friend" />
                    ))}
                  </section>
                )}
                <button
                  className="home-add"
                  aria-label={t("إضافة صديق")}
                  aria-describedby="home-add-help"
                  onClick={() =>
                    shell.open({ kind: "add-friend", step: "add" })
                  }
                >
                  <span className="home-add-icon" aria-hidden="true">
                    <UserPlus width={18} height={18} />
                  </span>
                  <span>
                    <strong>{t("إضافة صديق")}</strong>
                    <small id="home-add-help">
                      {t("باسم المستخدم أو برابط حسابك")}
                    </small>
                  </span>
                  <CaretLeft
                    className="directional"
                    width={16}
                    height={16}
                    aria-hidden="true"
                  />
                </button>
                {!shell.covered && <AccountReminder />}
              </div>
            )}
          </section>
        )}
        {!inviteId && !me && !guest && <HomeGuide paused={shell.covered} />}
      </main>
      {!me && !guest && !inviteId && (
        <InstallGuide
          signedIn={false}
          onSignIn={signIn}
          onRecovery={() => shell.openScreen("profile")}
        />
      )}
      {!me && (
        <footer className="site-footer">
          <span>
            <Wave />
            {t("مكالمات صوت وفيديو")}
          </span>
          <nav className="footer-links" aria-label={t("عن رنّة")}>
            <a href={`/privacy?lang=${locale}`}>{t("الخصوصية")}</a>
            <a href={`/terms?lang=${locale}`}>{t("شروط الاستخدام")}</a>
          </nav>
          <button onClick={shell.startMicrophoneCheck}>
            <Mic width={15} height={15} />
            {t("اختبار الميكروفون")}{" "}
          </button>
        </footer>
      )}
    </>
  );
}
